import getCustomerId from 'util/get-customer-id';

import type * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import getCurrencyConversionRate from 'data/get-currency-conversion-rate';
import getStorePublicDetails from 'data/get-store-public-details';
import type { Menu, SplashScreen, StoreDetails, WidgetOption } from 'types';

import { CustomerContext } from './customer-context';

interface IStoreContext {
  storeDetails: StoreDetails | undefined;
  setWidgetOption: (widgetOption: WidgetOption) => void;
  setSplashScreen: (splashScreen: SplashScreen) => void;
  setMenu: (menu: Menu) => void;
}

export const StoreContext = createContext<IStoreContext>({} as IStoreContext);

const StoreProvider: React.FC<React.ReactNode> = ({ children }) => {
  const { blocked, currency, setCustomerId } = useContext(CustomerContext);
  const [storeDetails, setStoreDetails] = useState<StoreDetails>();

  const setWidgetOption = (widgetOption: WidgetOption) => {
    setStoreDetails((currentStoreDetails) => {
      if (currentStoreDetails) {
        return { ...currentStoreDetails, widgetOption };
      }
    });
  };

  const setSplashScreen = (splashScreen: SplashScreen) => {
    setStoreDetails((currentStoreDetails) => {
      if (currentStoreDetails) {
        return { ...currentStoreDetails, splashScreen };
      }
    });
  };

  const setMenu = ({ offline, online }: Menu) => {
    setStoreDetails((currentStoreDetails) => {
      if (currentStoreDetails) {
        return { ...currentStoreDetails, offlineMenu: offline || [], onlineMenu: online || [] };
      }
    });
  };

  useEffect(() => {
    if (!storeDetails?.customerSetting?.getCustomerIdMapper) return;
    getCustomerId(storeDetails.customerSetting.getCustomerIdMapper).then(setCustomerId);
  }, [storeDetails]);

  useEffect(() => {
    const populateCurrency = async (_storeDetails?: StoreDetails): Promise<StoreDetails | undefined> => {
      if (!_storeDetails) return;
      if (!currency || !_storeDetails.currency.multiCurrency) {
        return { ..._storeDetails, currencyConversionRate: 1 };
      }
      const rate = await getCurrencyConversionRate({
        customerCurrencyValue: currency,
        shopCurrency: _storeDetails.currency
      });
      return { ..._storeDetails, currencyConversionRate: rate };
    };

    if (!blocked && currency !== undefined) {
      getStorePublicDetails().then(populateCurrency).then(setStoreDetails);
    }
  }, [blocked, currency]);

  const storeContextValue = useMemo(
    () => ({ setMenu, setSplashScreen, setWidgetOption, storeDetails }),
    [storeDetails]
  );

  return (
    <StoreContext.Provider value={storeContextValue}>
      {storeDetails?.widgetOption.enabled && children}
      {storeDetails === undefined && (
        <div
          style={{ display: window.sdkModeEnabled ? 'none' : 'block' }}
          className="lazy-spinner"
        />
      )}
    </StoreContext.Provider>
  );
};

export const useStoreDetails = (): StoreDetails => {
  const { storeDetails } = useContext(StoreContext);
  if (!storeDetails) {
    throw 'store details not ready to use';
  }
  return storeDetails;
};
export default StoreProvider;
